import { useState, useEffect } from 'react';
import { gamificationAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { Skeleton } from './ui/skeleton';
import { 
  Flame, Trophy, Star, Award, Target, Users, Medal, Crown,
  Zap, Phone, Calendar, Sunrise, Moon, TrendingUp
} from 'lucide-react';

// Badge icon mapping
const BADGE_ICONS = {
  'first_steps': Trophy,
  'getting_started': Star,
  'task_master': Star,
  'productivity_pro': Award,
  'task_legend': Crown,
  'on_fire': Flame,
  'unstoppable': Zap,
  'consistency_king': Medal,
  'meeting_maven': Calendar,
  'networking_ninja': Users,
  'call_champion': Phone,
  'phone_warrior': Phone,
  'early_bird': Sunrise,
  'night_owl': Moon,
  'perfect_week': Target
};

export const StreakBadge = ({ streak, className = '' }) => {
  if (!streak || streak < 1) return null;
  
  const getStreakColor = () => {
    if (streak >= 30) return 'bg-gradient-to-r from-orange-500 to-red-500';
    if (streak >= 7) return 'bg-gradient-to-r from-amber-500 to-orange-500';
    if (streak >= 3) return 'bg-gradient-to-r from-yellow-400 to-amber-500';
    return 'bg-amber-400';
  };
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white font-semibold text-sm ${getStreakColor()} ${className}`}>
      <Flame className="h-4 w-4 animate-pulse" />
      <span>{streak}-day streak!</span>
    </div>
  );
};

export const BadgeDisplay = ({ badge, size = 'md', showDetails = true }) => {
  const IconComponent = BADGE_ICONS[badge.id] || Award;
  const sizeClasses = {
    sm: 'h-8 w-8 text-lg',
    md: 'h-12 w-12 text-2xl',
    lg: 'h-16 w-16 text-3xl'
  };
  
  return (
    <div className="flex items-center gap-3" title={badge.description}>
      <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center border-2 border-amber-300 shadow-md`}>
        <span>{badge.icon}</span>
      </div>
      {showDetails && (
        <div>
          <p className="font-semibold text-sm">{badge.name}</p>
          <p className="text-xs text-muted-foreground">{badge.description}</p>
        </div>
      )}
    </div>
  );
};

export const GamificationWidget = ({ compact = false }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await gamificationAPI.getMyStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch gamification stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  if (compact) {
    return (
      <div className="flex items-center gap-4">
        <StreakBadge streak={stats.current_streak} />
        <div className="flex items-center gap-1 text-sm">
          <Trophy className="h-4 w-4 text-amber-500" />
          <span className="font-medium">{stats.badges_count} badges</span>
        </div>
        <div className="flex items-center gap-1 text-sm">
          <TrendingUp className="h-4 w-4 text-emerald-500" />
          <span className="font-medium">{stats.total_points} pts</span>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            Achievements
          </CardTitle>
          <StreakBadge streak={stats.current_streak} />
        </div>
        <CardDescription>
          {stats.total_points} total points • {stats.badges_count} badges earned
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Stats Overview */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <div className="text-lg font-bold text-emerald-700">{stats.total_tasks_completed}</div>
              <div className="text-xs text-muted-foreground">Tasks Done</div>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg">
              <div className="text-lg font-bold text-blue-700">{stats.total_meetings_attended}</div>
              <div className="text-xs text-muted-foreground">Meetings</div>
            </div>
            <div className="p-2 bg-purple-50 rounded-lg">
              <div className="text-lg font-bold text-purple-700">{stats.longest_streak}</div>
              <div className="text-xs text-muted-foreground">Best Streak</div>
            </div>
          </div>

          {/* Earned Badges */}
          {stats.badges_earned.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Badges Earned</h4>
              <div className="flex flex-wrap gap-2">
                {stats.badges_earned.slice(0, 6).map((badge) => (
                  <div 
                    key={badge.id}
                    className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center border-2 border-amber-300 shadow-sm cursor-pointer hover:scale-110 transition-transform"
                    title={`${badge.name}: ${badge.description}`}
                  >
                    <span className="text-lg">{badge.icon}</span>
                  </div>
                ))}
                {stats.badges_earned.length > 6 && (
                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center border-2 border-slate-200 text-xs font-semibold text-slate-600">
                    +{stats.badges_earned.length - 6}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const LeaderboardWidget = ({ period = 'weekly', limit = 5 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(period);

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedPeriod]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await gamificationAPI.getLeaderboard(selectedPeriod, limit);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <span className="text-xl">🥇</span>;
    if (rank === 2) return <span className="text-xl">🥈</span>;
    if (rank === 3) return <span className="text-xl">🥉</span>;
    return <span className="text-sm font-bold text-muted-foreground">#{rank}</span>;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Medal className="h-5 w-5 text-amber-500" />
            Leaderboard
          </CardTitle>
          <Tabs value={selectedPeriod} onValueChange={setSelectedPeriod} className="w-auto">
            <TabsList className="h-7">
              <TabsTrigger value="weekly" className="text-xs px-2 h-5">Week</TabsTrigger>
              <TabsTrigger value="monthly" className="text-xs px-2 h-5">Month</TabsTrigger>
              <TabsTrigger value="all_time" className="text-xs px-2 h-5">All Time</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : data?.leaderboard?.length > 0 ? (
          <ScrollArea className="h-[280px]">
            <div className="space-y-2">
              {data.leaderboard.map((entry) => (
                <div 
                  key={entry.user?.id}
                  className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                    entry.rank <= 3 ? 'bg-amber-50 border border-amber-200' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="w-8 flex justify-center">
                    {getRankBadge(entry.rank)}
                  </div>
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center font-semibold text-sm">
                    {entry.user?.first_name?.[0]}{entry.user?.last_name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {entry.user?.first_name} {entry.user?.last_name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{entry.tasks_completed} tasks</span>
                      {entry.streak > 0 && (
                        <span className="flex items-center gap-0.5 text-orange-500">
                          <Flame className="h-3 w-3" />
                          {entry.streak}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-600">{entry.points}</p>
                    <p className="text-xs text-muted-foreground">points</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Medal className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No data yet. Complete tasks to appear on the leaderboard!</p>
          </div>
        )}
        
        {data?.my_rank && data.my_rank > limit && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-sm text-muted-foreground text-center">
              Your rank: <span className="font-bold text-primary">#{data.my_rank}</span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const BadgesPanel = () => {
  const [stats, setStats] = useState(null);
  const [allBadges, setAllBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, badgesRes] = await Promise.all([
        gamificationAPI.getMyStats(),
        gamificationAPI.getAllBadges()
      ]);
      setStats(statsRes.data);
      setAllBadges(badgesRes.data);
    } catch (error) {
      console.error('Failed to fetch badges:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-20 rounded-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const earnedIds = new Set(stats?.badges_earned?.map(b => b.id) || []);
  const categories = ['tasks', 'streak', 'meetings', 'calls', 'special'];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5 text-amber-500" />
          All Badges
        </CardTitle>
        <CardDescription>
          {stats?.badges_count || 0} of {allBadges.length} badges earned
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="tasks">
          <TabsList className="w-full justify-start mb-4">
            {categories.map(cat => (
              <TabsTrigger key={cat} value={cat} className="capitalize">
                {cat}
              </TabsTrigger>
            ))}
          </TabsList>
          
          {categories.map(category => (
            <TabsContent key={category} value={category}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {allBadges
                  .filter(b => b.category === category)
                  .map(badge => {
                    const isEarned = earnedIds.has(badge.id);
                    return (
                      <div 
                        key={badge.id}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          isEarned 
                            ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100' 
                            : 'border-slate-200 bg-slate-50 opacity-60'
                        }`}
                      >
                        <div className="text-center">
                          <div className={`text-4xl mb-2 ${isEarned ? '' : 'grayscale'}`}>
                            {badge.icon}
                          </div>
                          <p className="font-semibold text-sm">{badge.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                          {isEarned && (
                            <Badge className="mt-2 bg-emerald-500">Earned!</Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default GamificationWidget;
